import React, { memo } from 'react';

// --- ICONOS DE ORDENAMIENTO EN SVG ---
const IconChevronUp = () => (
    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
        <polyline points="18 15 12 9 6 15"/>
    </svg>
);

const IconChevronDown = () => (
    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
        <polyline points="6 9 12 15 18 9"/>
    </svg>
);

const IconSortAlt = () => (
    <svg className="w-3 h-3 opacity-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M7 15l5 5 5-5M7 9l5-5 5 5"/>
    </svg>
);

/**
 * SortableHeader - Componente visual reutilizable y optimizado para las cabeceras de tablas.
 * Soporta ordenamiento dinámico, visibilidad adaptativa y memoización automática.
 * * @param {string} label - El texto visual que se mostrará en la cabecera.
 * @param {string} sortKey - Clave única de datos asociada a esta columna para ordenamiento.
 * @param {object} sortConfig - Configuración activa de ordenamiento { key, direction }.
 * @param {object} columnVisibility - Mapa de visibilidad de las columnas { [key]: boolean }.
 * @param {function} onSort - Callback encargado de despachar el evento de ordenamiento (ej: handleSort).
 * @param {string} className - Clases de estilo adicionales de Tailwind CSS.
 */
const SortableHeader = memo(({ 
    label, 
    sortKey, 
    sortConfig, 
    columnVisibility, 
    onSort, 
    className = "",
    onResizeStart = null
}) => {
    // Validación de visibilidad: si la columna está oculta por el selector, no se renderiza nada en el DOM
    if (sortKey && columnVisibility && !columnVisibility[sortKey]) return null;
    
    const isActive = sortConfig?.key === sortKey;
    
    return (
        <th 
            onClick={() => sortKey && onSort?.(sortKey)}
            className={`relative sticky top-0 z-40 p-4 ${
                sortKey ? 'cursor-pointer hover:bg-slate-800' : ''
            } border-b border-r border-slate-700 uppercase tracking-wider text-[10px] font-semibold transition-colors bg-slate-900 ${className}`}
        >
            <div className="flex items-center justify-center gap-2 text-center text-white">
                <span>{label}</span>
                {sortKey && (
                    <div className="flex flex-col text-white">
                        {isActive ? (
                            sortConfig.direction === 'ascending' ? <IconChevronUp /> : <IconChevronDown />
                        ) : (
                            <IconSortAlt />
                        )}
                    </div>
                )}
            </div>

            {/* Tirador de redimensionado. Solo aparece si la tabla lo habilita pasando
                onResizeStart, así los tableros que no lo usan no cambian en nada.
                El stopPropagation evita que arrastrar el borde dispare el ordenamiento. */}
            {onResizeStart && (
                <div
                    onMouseDown={(e) => onResizeStart(e)}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-sky-400/70 active:bg-sky-400 transition-colors"
                    title="Arrastrar para cambiar el ancho"
                />
            )}
        </th>
    );
});

SortableHeader.displayName = 'SortableHeader';

export default SortableHeader;